import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/Auth/AuthContext';
import { useRegions } from '../context/RegionContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Common/Layout';
import CustomDatePicker from '../components/Common/DatePicker';
import {
  createAgent,
  updateAgent,
  deleteAgent,
  getAgents,
  getRoles,
  updateUserStatus,
} from "../components/Api/api";
import { toast } from "react-toastify";
import { FaEdit, FaUserSlash, FaUserCheck } from "react-icons/fa";
import Select from "react-select";

// Remove initialAgents since we'll fetch from API

const AgentManagementPage = () => {
  const { user, refreshTrigger } = useAuth();
  const { regions: contextRegions } = useRegions();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(50);
  const [currentAgent, setCurrentAgent] = useState({
    name: "",
    role: "",
    phoneNumber: "",
    personalEmail: "",
    doj: "",
    region: [],
    regionIds: []
  });
  const [successMessage, setSuccessMessage] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAgentForStatus, setSelectedAgentForStatus] = useState(null);
  
  
  const regionOptions = contextRegions.map((region) => ({
    value: region.id,
    label: region.name,
  }));

  // Fetch agents and roles on component mount or page change
  useEffect(() => {
    const fetchData = async () => {
      try {
        setTableLoading(true);
        // console.log('Fetching data...');
        
        // Get roles and agents
        const [rolesData, agentsResponse] = await Promise.all([
          getRoles(),
          getAgents(currentPage, itemsPerPage)
        ]);
        
        // console.log('Raw agents response:', agentsResponse);
        
        // Ensure agentsResponse is treated as an array
        const agentsData = Array.isArray(agentsResponse.items) ? agentsResponse.items : [];
        setTotalPages(Math.ceil(agentsResponse.total / itemsPerPage));
        
        // console.log('Processed agents data:', agentsData);
        console.log("Region data:", contextRegions);
        
        const formattedAgents = agentsData.map((agent) => {
          // console.log('Processing agent:', agent);
          const userData = agent.user || {};
          console.log('🔍 Processing agent:', {
            agentId: agent.id,
            agentName: userData.name,
            agentEmail: userData.email,
            regionIds: agent.region_ids
          });

          const agentRegions = (agent.region_ids || []).map((id) => contextRegions.find((r) => String(r.id) === String(id))).filter(Boolean);
          const regionAccess = agentRegions.map((r) => r.name).join(", ");
          const regionAccessIds = agentRegions.map((r) => r.id);
          return {
            id: agent.id,
            name: userData.name || "",
            phoneNumber: userData.phone || "",
            personalEmail: userData.email || "",
            doj: agent.date_of_joining || "",
            role: agent.role_group?.id?.toString() || "",
            region: regionAccess || "",
            regionIds: regionAccessIds,
            roleGroupName: agent.role_group?.name || "No Role Assigned",
            status: userData.is_active,
          };
        });

        // console.log('Formatted agents:', formattedAgents);
        
        setAgents(formattedAgents);
        setRoles(rolesData);
        setError(null);
      } catch (err) {
        console.error('Error details:', err);
        setError('Failed to fetch data. Please try again later.');
      } finally {
        setTableLoading(false);
      }
    };
    
    if (contextRegions.length>0) {
      // setLoading(false);
      fetchData();
    } 
    
  }, [currentPage, refreshTrigger, contextRegions]);

  const validateForm = () => {
    const errors = {};
    if (!currentAgent.name.trim()) errors.name = 'Name is required';
    if (!currentAgent.role) errors.role = 'Role is required';
    if (!currentAgent.phoneNumber.trim()) errors.phoneNumber = 'Phone number is required';
    if (!currentAgent.personalEmail.trim()) errors.personalEmail = 'Email is required';
    if (!currentAgent.doj) errors.doj = 'Date of joining is required';
    if (!currentAgent.region) errors.region = 'Region is required';
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (currentAgent.personalEmail && !emailRegex.test(currentAgent.personalEmail)) {
      errors.personalEmail = 'Please enter a valid email address';
    }
    
    // Phone number validation (assuming Indian phone numbers)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (currentAgent.phoneNumber && !phoneRegex.test(currentAgent.phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentAgent(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDateChange = (date) => {
    setCurrentAgent(prev => ({
      ...prev,
      doj: date ? date.toISOString().split('T')[0] : ''
    }));
    // Clear error when user selects a date
    if (formErrors.doj) {
      setFormErrors(prev => ({
        ...prev,
        doj: ''
      }));
    }
  };
  
  const refershDetails = async() => {
    // Refresh the agents list
      const agentsResponse = await getAgents();
      const agentsData = Array.isArray(agentsResponse.items) ? agentsResponse.items : [];
      const formattedAgents = agentsData.map(agent => {
      const userData = agent.user || {};
      console.log('🔄 Refreshing agent:', {
        agentId: agent.id,
        agentName: userData.name,
        agentEmail: userData.email,
        regionIds: agent.region_ids
      });
      
      const agentRegions = (agent.region_ids || []).map((id) => contextRegions.find((r) => String(r.id) === String(id))).filter(Boolean);
      const regionAccess = agentRegions.map((r) => r.name).join(", ");
      const regionAccessIds = agentRegions.map((r) => r.id);
      return {
        id: agent.id,
        name: userData.name || "",
        phoneNumber: userData.phone || "",
        personalEmail: userData.email || "",
        doj: agent.date_of_joining || "",
        role: agent.role_group?.id?.toString() || "",
        region: regionAccess || "",
        regionIds: regionAccessIds,
        roleGroupName: agent.role_group?.name || "No Role Assigned",
        status: userData.is_active,
      };
      });
      setAgents(formattedAgents);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setTableLoading(true);
      await createAgent(currentAgent);

      if (isEditing) {
        toast.success('Agent updated successfully!');
      } else {
        toast.success('Agent created successfully!');
      }
      resetForm();
      setError(null);
      
      await refershDetails();
    } catch (err) {
      setError('Failed to save agent. Please try again.');
      console.error('Error saving agent:', err);
    } finally {
      setTableLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentAgent({
      name: "",
      role: "",
      phoneNumber: "",
      personalEmail: "",
      doj: "",
      region: [],
      regionIds: []
    });
    setIsEditing(false);
  };

  const handleEdit = (agent) => {
    setCurrentAgent(agent);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteAgent(id);
      setAgents(agents.filter(agent => agent.id !== id));
      setError(null);
    } catch (err) {
      setError('Failed to delete agent. Please try again.');
      console.error('Error deleting agent:', err);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Add search filter function
  const filteredAgents = agents.filter(agent =>
    agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.personalEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.phoneNumber.includes(searchQuery) ||
    agent.roleGroupName.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  
  const setSelectedAgent = (agent) => {
    const selectedRegions = contextRegions
      .filter((r) => agent.regionIds.includes(r.id))
      .map((r) => ({ value: r.id, label: r.name }));

    setCurrentAgent({
      name: agent.name,
      role: agent.role,
      phoneNumber: agent.phoneNumber,
      personalEmail: agent.personalEmail,
      doj: agent.doj,
      region: selectedRegions,
      regionIds: selectedRegions.map((r) => r.value)
    });

    setIsEditing(true);
  };
  
  const handleToggleStatus = async (user_id, status) => {
    setSelectedAgentForStatus({ id: user_id, status });
    setShowConfirmModal(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedAgentForStatus) return;

    try {
      setTableLoading(true);
      const { id, status } = selectedAgentForStatus;
      await updateUserStatus(id, !status);
      const action = status ? "deactivated" : "activated";
      toast.success(`Agent ${action} successfully!`);
      await refershDetails();
    } catch (error) {
      toast.error(error?.response?.data?.errors[0]?.message || "Something went wrong!");
    } finally {
      setTableLoading(false);
      setShowConfirmModal(false);
      setSelectedAgentForStatus(null);
    }
  };

  // Confirmation Modal Component
  const ConfirmationModal = ({ isOpen, onClose, onConfirm, action }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
          <div className="text-center">
            {action === 'deactivate' ? (
              <FaUserSlash className="mx-auto h-12 w-12 text-red-500 mb-4" />
            ) : (
              <FaUserCheck className="mx-auto h-12 w-12 text-green-500 mb-4" />
            )}
            <h3 className="text-lg font-medium text-gray-900 mb-2">Confirm {action === 'deactivate' ? 'Deactivation' : 'Activation'}</h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to {action} this agent? This action can be reversed later.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                action === 'deactivate'
                  ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
              }`}
            >
              {action === 'deactivate' ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Agent Management</h1>

        {error && <div className="text-red-500 mb-4">{error}</div>}
        {successMessage && (
          <div className="text-green-500 mb-4">{successMessage}</div>
        )}

        {/* Agent Form */}
        <form onSubmit={handleSubmit} className="mb-8" noValidate>
          {/* Basic Info Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <label className="block text-[#374151] text-sm mb-2">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={currentAgent.name}
                onChange={handleInputChange}
                className="w-full h-10 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent text-[#374151] placeholder-gray-400 bg-white"
                placeholder="Enter name"
                required
              />
              {formErrors.name && (
                <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-[#374151] text-sm mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={currentAgent.phoneNumber}
                onChange={handleInputChange}
                disabled={isEditing}
                className="w-full h-10 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent text-[#374151] placeholder-gray-400 bg-white"
                placeholder="Enter phone number"
                required
              />
              {formErrors.phoneNumber && (
                <p className="text-red-500 text-sm mt-1">
                  {formErrors.phoneNumber}
                </p>
              )}
            </div>

            <div>
              <label className="block text-[#374151] text-sm mb-2">
                Personal Email *
              </label>
              <input
                type="email"
                name="personalEmail"
                value={currentAgent.personalEmail}
                onChange={handleInputChange}
                // disabled={isEditing}
                className="w-full h-10 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent text-[#374151] placeholder-gray-400 bg-white"
                placeholder="Enter email address"
                required
              />
              {formErrors.personalEmail && (
                <p className="text-red-500 text-sm mt-1">
                  {formErrors.personalEmail}
                </p>
              )}
            </div>

            <div>
              <label className="block text-[#374151] text-sm mb-2">
                Date of Joining *
              </label>
              <CustomDatePicker
                selected={currentAgent.doj ? new Date(currentAgent.doj) : null}
                onChange={handleDateChange}
                placeholderText="Select date of joining"
                maxDate={new Date()}
                showIcon={true}
                error={formErrors.doj}
              />
            </div>

            <div>
              <label className="block text-[#374151] text-sm mb-2">
                Role *
              </label>
              <select
                name="role"
                value={currentAgent.role}
                onChange={handleInputChange}
                className="w-full h-10 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent text-[#374151] placeholder-gray-400 bg-white appearance-none"
                required
              >
                <option value="">Select Role</option>
                {roles.filter((role) => role.id !== 18 || currentAgent.role === "18").map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              {formErrors.role && (
                <p className="text-red-500 text-sm mt-1">{formErrors.role}</p>
              )}
            </div>

            <div>
              <label className="block text-[#374151] text-sm mb-2">
                Region *
              </label>
              <Select
                isMulti
                name="region"
                options={regionOptions}
                value={currentAgent.region}
                onChange={(selectedOptions) => {
                  setCurrentAgent((prev) => ({
                    ...prev,
                    region: selectedOptions,
                    regionIds: selectedOptions.map((opt) => opt.value),
                  }));
                  if (formErrors.region) {
                    setFormErrors((prev) => ({ ...prev, region: "" }));
                  }
                }}
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="Select Region"
                required
              />
              {formErrors.region && (
                <p className="text-red-500 text-sm mt-1">{formErrors.region}</p>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="mt-6">
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2.5 bg-[#6366F1] text-white text-sm font-medium rounded-lg hover:bg-[#4F46E5] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating...
                </span>
              ) : isEditing ? (
                "Update Agent"
              ) : (
                "Create Agent"
              )}
            </button>
            {isEditing && (
              <button
                type="button"
                onClick={resetForm}
                className="ml-3 px-6 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        {/* Search Bar and Pagination Info */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <input
                type="text"
                placeholder="Search agents by name, email or phone..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </div>
        </div>

        {/* Agents Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border rounded-lg">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date of Joining
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Region Names
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Edit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tableLoading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAgents.map((agent) => (
                  <tr key={agent.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {agent.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {agent.phoneNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {agent.personalEmail}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{agent.doj}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {agent.roleGroupName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {agent.region}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => setSelectedAgent(agent)}
                        className="text-green-600 hover:text-green-900"
                        title="Edit Access"
                      >
                        <FaEdit className="w-5 h-5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        className={`inline-flex items-center gap-2 text-white text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 px-4 py-2
                        ${
                          agent.status
                            ? 'bg-red-500 hover:bg-red-600 focus:ring-red-500 shadow-sm hover:shadow-md'
                            : 'bg-green-500 hover:bg-green-600 focus:ring-green-500 shadow-sm hover:shadow-md'
                        }`}
                        onClick={() => handleToggleStatus(agent.id, agent.status)}
                        title={agent.status ? "Deactivate Agent" : "Activate Agent"}
                      >
                        {agent.status ? (
                          <>
                            <FaUserSlash className="w-4 h-4" />
                            <span>Deactivate</span>
                          </>
                        ) : (
                          <>
                            <FaUserCheck className="w-4 h-4" />
                            <span>Activate</span>
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-center mt-6 gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
            }`}
          >
            Previous
          </button>
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index + 1}
              onClick={() => handlePageChange(index + 1)}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                currentPage === index + 1
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
              }`}
            >
              {index + 1}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              currentPage === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* Add the modal component before the closing Layout tag */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setSelectedAgentForStatus(null);
        }}
        onConfirm={handleConfirmStatusChange}
        action={selectedAgentForStatus?.status ? 'deactivate' : 'activate'}
      />
    </Layout>
  );
};

export default AgentManagementPage; 